/**
 * Maxmark Agency - REST API Client for cPanel Backend
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Generic fetch wrapper with JSON error handling.
 * @param {string} endpoint
 * @param {RequestInit} [options]
 * @param {string|null} [tokenKey] localStorage key holding the Bearer token to attach, if any
 */
async function request(endpoint, options = {}, tokenKey = 'maxmark_admin_token') {
  const url = `${API_BASE}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  const token = tokenKey ? localStorage.getItem(tokenKey) : null;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorMsg = data.error || `HTTP error! Status: ${response.status}`;
    throw new Error(errorMsg);
  }

  return data;
}

export const api = {
  // Public Lead Endpoints
  submitContact: (payload) => request('/leads/contact', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  // Attaches the client token when present (Client Portal request-tracker
  // form) but works equally well anonymously (public Intelligence Review form).
  submitReview: (payload) => request('/leads/review', {
    method: 'POST',
    body: JSON.stringify(payload)
  }, 'maxmark_client_token'),

  subscribeNewsletter: (email) => request('/newsletter', {
    method: 'POST',
    body: JSON.stringify({ email })
  }, null),

  // Admin Endpoints
  adminLogin: (credentials) => request('/admin/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }, null),

  adminVerify: () => request('/admin/verify', {
    method: 'GET'
  }),

  getLeads: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/admin/leads${queryString ? `?${queryString}` : ''}`, {
      method: 'GET'
    });
  },

  updateLeadStatus: (leadId, status) => request(`/admin/leads/${leadId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  }),

  getExportUrl: () => `${API_BASE}/admin/export`,

  // Admin: Client Intelligence Portal management
  getIntelCompetitors: () => request('/admin/intel/competitors', { method: 'GET' }),
  createIntelCompetitor: (payload) => request('/admin/intel/competitors', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  updateIntelCompetitor: (id, payload) => request(`/admin/intel/competitors/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),
  deleteIntelCompetitor: (id) => request(`/admin/intel/competitors/${id}`, { method: 'DELETE' }),

  getIntelAds: (competitorId = 'all') => request(`/admin/intel/ads?competitor_id=${encodeURIComponent(competitorId)}`, { method: 'GET' }),
  createIntelAd: (payload) => request('/admin/intel/ads', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  updateIntelAd: (id, payload) => request(`/admin/intel/ads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),
  deleteIntelAd: (id) => request(`/admin/intel/ads/${id}`, { method: 'DELETE' }),

  getIntelClients: () => request('/admin/intel/clients', { method: 'GET' }),
  createIntelClient: (payload) => request('/admin/intel/clients', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  setIntelClientAssignment: (clientId, competitorId, assigned) => request(`/admin/intel/clients/${clientId}/assignments`, {
    method: 'PATCH',
    body: JSON.stringify({ competitor_id: competitorId, assigned })
  }),

  getIntelRequests: () => request('/admin/intel/requests', { method: 'GET' }),
  approveIntelRequest: (leadId) => request(`/admin/intel/requests/${leadId}/approve`, { method: 'POST' }),

  // Admin: CMS
  getCmsPages: () => request('/admin/cms/pages', { method: 'GET' }),
  updateCmsPage: (id, payload) => request(`/admin/cms/pages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),

  getCmsPlans: () => request('/admin/cms/plans', { method: 'GET' }),
  updateCmsPlan: (id, payload) => request(`/admin/cms/plans/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),

  getCmsCaseStudies: () => request('/admin/cms/case-studies', { method: 'GET' }),
  createCmsCaseStudy: (payload) => request('/admin/cms/case-studies', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  updateCmsCaseStudy: (id, payload) => request(`/admin/cms/case-studies/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),
  deleteCmsCaseStudy: (id) => request(`/admin/cms/case-studies/${id}`, { method: 'DELETE' }),

  getCmsArticles: () => request('/admin/cms/articles', { method: 'GET' }),
  createCmsArticle: (payload) => request('/admin/cms/articles', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  updateCmsArticle: (id, payload) => request(`/admin/cms/articles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),

  getNewsletterSubscribers: () => request('/admin/newsletter', { method: 'GET' }),
  getNewsletterExportUrl: () => `${API_BASE}/admin/newsletter/export`,

  getSettingsStatus: () => request('/admin/settings/status', { method: 'GET' }),

  // Public Content (marketing site)
  getPublicPlans: () => request('/plans', { method: 'GET' }, null),
  getPublicCaseStudies: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/case-studies${queryString ? `?${queryString}` : ''}`, { method: 'GET' }, null);
  },
  getPublicArticles: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/articles${queryString ? `?${queryString}` : ''}`, { method: 'GET' }, null);
  },

  // Client Portal Endpoints
  clientLogin: (credentials) => request('/client/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }, null),
  clientVerify: () => request('/client/verify', { method: 'GET' }, 'maxmark_client_token'),
  getClientCompetitors: () => request('/client/competitors', { method: 'GET' }, 'maxmark_client_token'),
  getClientAds: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/client/ads${queryString ? `?${queryString}` : ''}`, { method: 'GET' }, 'maxmark_client_token');
  },
  getClientAlerts: () => request('/client/alerts', { method: 'GET' }, 'maxmark_client_token'),
  getClientRequests: () => request('/client/requests', { method: 'GET' }, 'maxmark_client_token'),
  getClientPreferences: () => request('/client/preferences', { method: 'GET' }, 'maxmark_client_token'),
  updateClientPreferences: (preferences) => request('/client/preferences', {
    method: 'PATCH',
    body: JSON.stringify(preferences)
  }, 'maxmark_client_token')
};

export default api;
